import React, { useEffect, useMemo, useState } from 'react';
import { HStack, Input, Text, VStack } from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { providerValidationSchema } from '@/validation/provider';
import useToast from '@/hooks/useToast';
import {
  APPOINTMENT_TYPES,
  AVAILABILITIES,
  DESIGNATIONS,
  MODALITIES,
  SCHEDULE_TOOLS,
  SPECIALTIES,
  TProvider,
} from '@/types/Provider.types';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import DropdownMultiSelect from '@/components/atoms/Dropdown/DropdownMultiSelect';
import ProfilePhotoInput from '@/components/molecules/ProfilePhotoInput';
import Dropdown from '@/components/atoms/Dropdown';
import CustomButton from '@/components/atoms/CustomButton';
import { isEqual, isUndefined } from 'lodash';
import ProviderLocationUpdateModalWithTrigger from './ProviderLocationUpdateModalWithTrigger';
import { TUserLocation } from '@/types/User.types';

type Props = {
  providerId?: string;
  isPending?: boolean;
  onSubmit?: (form: TProvider, photo: File | null) => void;
  submitLabel?: string;
  defaultValues?: TProvider;
  isPhotoRequired?: boolean;
  refetchProvider?: () => void;
};

const ProviderForm = ({
  providerId,
  isPending,
  onSubmit,
  defaultValues,
  submitLabel = 'Create Provider',
  isPhotoRequired = true,
  refetchProvider,
}: Props) => {
  const toast = useToast();

  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);

  const [locations, setLocations] = useState<TUserLocation[] | undefined>(defaultValues?.locations);

  // updates the local locations state with newly received defaultValues for location every time they are updated (e.g. when the existing provider is re-fetched)
  useEffect(() => {
    if (defaultValues?.locations) {
      setLocations(defaultValues?.locations);
    }
  }, [defaultValues?.locations]);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(providerValidationSchema),
    defaultValues: {
      designation: [],
      specialities: [],
      appointment_types: [],
      availability: undefined,
      ...defaultValues,
    },
  });

  const {
    handleSubmit,
    watch,
    register,
    setValue,
    formState: { errors, touchedFields, isValid, isDirty },
  } = methods;

  const scheduleSyncInterestValue = watch('sync_schedule_interest');

  const designationsValue = watch('designation');
  const isDesignationCustom = designationsValue?.includes('OTHER');
  const customDesignationValue = watch('custom_designation');

  const scheduleToolsValue = watch('schedule_tools');
  const isScheduleToolCustom = scheduleToolsValue?.includes('OTHER');
  const customScheduleTool = watch('custom_schedule_tool');

  const appointmentTypesValue = watch('appointment_types');
  const isAppointmentTypeCustom = appointmentTypesValue?.includes('OTHER');
  const customAppointmentType = watch('custom_appointment_type');

  const specialitiesValue = watch('specialities');
  const isSpecialtiesCustom = specialitiesValue?.includes('OTHER');
  const customSpecialtyValue = watch('custom_speciality');

  const availabilityValue = watch('availability');
  const modalitiesValue = watch('modalities');

  const isSomeOfDropdownsDirty =
    !isEqual(appointmentTypesValue, defaultValues?.appointment_types) ||
    !isEqual(specialitiesValue, defaultValues?.specialities) ||
    !isEqual(designationsValue, defaultValues?.designation);

  const isSyncingScheduleDirty = scheduleSyncInterestValue !== defaultValues?.sync_schedule_interest;
  const isProfileImageDirty = profilePhotoFile !== null;

  const isFormDirty = isDirty || isSomeOfDropdownsDirty || isSyncingScheduleDirty || isProfileImageDirty;

  const isSpecialtiesEmpty = !specialitiesValue?.length;
  const isDesignationEmpty = !designationsValue.length;
  const isAvailabilityEmpty = availabilityValue === undefined;
  const isSomeRequiredDropdownEmpty = isSpecialtiesEmpty || isDesignationEmpty || isAvailabilityEmpty;

  const dropdownsErrorMessage = isDesignationEmpty
    ? "Designations can't be empty."
    : isSpecialtiesEmpty
      ? "Specialization can't be empty."
      : isAvailabilityEmpty
        ? "Availability can't be empty"
        : '';

  const isSomeOtherValueMistakenlyEmpty = useMemo(() => {
    let someOtherValueEmpty = false;

    if (isScheduleToolCustom && !customScheduleTool) someOtherValueEmpty = true;
    if (isDesignationCustom && !customDesignationValue) someOtherValueEmpty = true;
    if (isAppointmentTypeCustom && !customAppointmentType) someOtherValueEmpty = true;
    if (isSpecialtiesCustom && !customSpecialtyValue) someOtherValueEmpty = true;

    return someOtherValueEmpty;
  }, [
    isScheduleToolCustom,
    isDesignationCustom,
    isAppointmentTypeCustom,
    isSpecialtiesCustom,
    customScheduleTool,
    customDesignationValue,
    customAppointmentType,
    customSpecialtyValue,
  ]);

  // const noLocationsAdded = !locations || locations.length === 0;

  const isSubmitDisabled =
    !isValid || !isFormDirty || isPending || isSomeRequiredDropdownEmpty || isSomeOtherValueMistakenlyEmpty;

  const onFormSubmit = (data: unknown) => {
    if (profilePhotoFile === null && isPhotoRequired) {
      toast({ status: 'error', title: 'Form error', description: 'Profile photo is required.' });
      return;
    }

    const form = data as TProvider;
    form.locations = locations;

    if (onSubmit) {
      onSubmit(form, profilePhotoFile);
    }
  };

  return (
    <VStack flex={1} width={'100%'}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <VStack gap={'20px'} marginBottom={'40px'}>
            <HStack gap={'20px'}>
              <Input
                {...register('first_name', { required: true })}
                className="custom-input"
                placeholder={'First Name (required)'}
                fontSize={'16px'}
                _placeholder={{
                  fontWeight: '600',
                  color: 'background.blueGray',
                }}
                width={FORM_FIELD_WIDTH}
                data-state={touchedFields.first_name ? (errors.first_name?.message ? 'invalid' : 'valid') : ''}
              />
              <Input
                {...register('last_name', { required: true })}
                className="custom-input"
                placeholder={'Last Name (required)'}
                fontSize={'16px'}
                _placeholder={{
                  fontWeight: '600',
                  color: 'background.blueGray',
                }}
                width={FORM_FIELD_WIDTH}
                data-state={touchedFields.last_name ? (errors.last_name?.message ? 'invalid' : 'valid') : ''}
              />
            </HStack>
            <HStack gap={'20px'}>
              <Input
                {...register('practice_name')}
                className="custom-input"
                placeholder={'Practice Name (optional)'}
                fontSize={'16px'}
                _placeholder={{
                  fontWeight: '600',
                  color: 'background.blueGray',
                }}
                width={FORM_FIELD_WIDTH}
                data-state={touchedFields.practice_name ? (errors.practice_name?.message ? 'invalid' : 'valid') : ''}
              />
              <Input
                {...register('email')}
                className="custom-input"
                placeholder={'Email (optional)'}
                fontSize={'16px'}
                _placeholder={{
                  fontWeight: '600',
                  color: 'background.blueGray',
                }}
                width={FORM_FIELD_WIDTH}
                data-state={touchedFields.email ? (errors.email?.message ? 'invalid' : 'valid') : ''}
              />
            </HStack>

            <ProviderLocationUpdateModalWithTrigger
              providerId={providerId}
              currentLocations={locations}
              onSuccess={providerId ? refetchProvider : undefined}
              setLocationsValue={setLocations}
            />

            <HStack gap={'20px'} width={'100%'}>
              <DropdownMultiSelect
                title="Designations"
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={DESIGNATIONS}
                selectedOptions={designationsValue?.filter((value): value is string => value !== undefined)}
                onOptionClick={(option) => {
                  const copy = [...designationsValue];
                  if (copy.includes(option)) {
                    copy.splice(copy.indexOf(option), 1);
                  } else {
                    copy.push(option);
                  }
                  setValue('designation', copy, { shouldValidate: true, shouldDirty: true });
                }}
              />
              {isDesignationCustom && (
                <Input
                  {...register('custom_designation')}
                  className="custom-input"
                  placeholder={'Other Designation'}
                  fontSize={'16px'}
                  _placeholder={{
                    fontWeight: '600',
                    color: 'background.blueGray',
                  }}
                  data-state={
                    touchedFields.custom_designation ? (errors.custom_designation?.message ? 'invalid' : 'valid') : ''
                  }
                />
              )}
            </HStack>

            <HStack gap={'20px'} width={'100%'}>
              <DropdownMultiSelect
                title="Specialization"
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={SPECIALTIES}
                selectedOptions={specialitiesValue?.filter((value): value is string => value !== undefined)}
                onOptionClick={(option) => {
                  const copy = [...(specialitiesValue ?? [])];
                  if (copy.includes(option)) {
                    copy.splice(copy.indexOf(option), 1);
                  } else {
                    copy.push(option);
                  }
                  setValue('specialities', copy, { shouldValidate: true, shouldDirty: true });
                }}
              />
              {isSpecialtiesCustom && (
                <Input
                  {...register('custom_speciality')}
                  className="custom-input"
                  placeholder={'Other Specialization'}
                  fontSize={'16px'}
                  _placeholder={{
                    fontWeight: '600',
                    color: 'background.blueGray',
                  }}
                  data-state={
                    touchedFields.custom_speciality ? (errors.custom_speciality?.message ? 'invalid' : 'valid') : ''
                  }
                />
              )}
            </HStack>

            <HStack gap={'20px'} width={'100%'}>
              <Dropdown
                formatValues
                shouldShowTitleOnTop
                title="Availability"
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={AVAILABILITIES}
                selectedOption={availabilityValue?.toString()}
                onOptionClick={(option) => {
                  setValue('availability', option, { shouldValidate: true, shouldDirty: true });
                }}
              />
            </HStack>

            <HStack gap={'20px'} width={'100%'}>
              <DropdownMultiSelect
                title="Modalities"
                formatOption={false}
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={MODALITIES}
                selectedOptions={modalitiesValue?.filter((value): value is string => value !== undefined)}
                onOptionClick={(option) => {
                  const copy = [...(modalitiesValue ?? [])];
                  if (copy.includes(option)) {
                    copy.splice(copy.indexOf(option), 1);
                  } else {
                    copy.push(option);
                  }
                  setValue('modalities', copy, { shouldValidate: true, shouldDirty: true });
                }}
              />
            </HStack>

            <Input
              title="Bio"
              {...register('bio')}
              className="custom-input"
              as={'textarea'}
              placeholder={'About Me (optional)'}
              minWidth={FORM_FIELD_WIDTH}
              style={{ minHeight: '200px', maxHeight: '400px' }}
              _placeholder={{ fontWeight: '600', color: 'text.mediumGray' }}
              data-state={touchedFields.bio ? (errors.bio?.message ? 'invalid' : 'valid') : ''}
            />

            <Input
              {...register('population')}
              className="custom-input"
              as={'textarea'}
              placeholder={'Population (optional)'}
              minWidth={FORM_FIELD_WIDTH}
              style={{ minHeight: '100px', maxHeight: '400px' }}
              _placeholder={{ fontWeight: '600', color: 'text.mediumGray' }}
              data-state={touchedFields.bio ? (errors.bio?.message ? 'invalid' : 'valid') : ''}
            />

            <ProfilePhotoInput
              setProfilePhoto={setProfilePhotoFile}
              width="100%"
              initialPreviewUrl={defaultValues?.profile_photo as string}
            />
            <HStack gap={'20px'} width={'100%'}>
              <DropdownMultiSelect
                title="Scheduling Tools"
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={SCHEDULE_TOOLS}
                selectedOptions={scheduleToolsValue?.filter((value): value is string => value !== undefined)}
                onOptionClick={(option) => {
                  const copy = [...(scheduleToolsValue ?? [])];
                  if (copy.includes(option)) {
                    copy.splice(copy.indexOf(option), 1);
                  } else {
                    copy.push(option);
                  }
                  setValue('schedule_tools', copy, { shouldValidate: true, shouldDirty: true });
                }}
              />
              {isScheduleToolCustom && (
                <Input
                  {...register('custom_schedule_tool')}
                  className="custom-input"
                  placeholder={'Other Schedule Tool'}
                  fontSize={'16px'}
                  _placeholder={{
                    fontWeight: '600',
                    color: 'background.blueGray',
                  }}
                  data-state={
                    touchedFields.custom_schedule_tool
                      ? errors.custom_schedule_tool?.message
                        ? 'invalid'
                        : 'valid'
                      : ''
                  }
                />
              )}
            </HStack>
            <HStack gap={'20px'}>
              <Input
                {...register('ehr_software')}
                className="custom-input"
                placeholder={'EHR Software (optional)'}
                fontSize={'16px'}
                _placeholder={{
                  fontWeight: '600',
                  color: 'background.blueGray',
                }}
                width={FORM_FIELD_WIDTH}
                data-state={touchedFields.ehr_software ? (errors.ehr_software?.message ? 'invalid' : 'valid') : ''}
              />
              <Dropdown
                title="Interested In Syncing Schedule"
                shouldShowTitleOnTop
                height="50px"
                width={FORM_FIELD_WIDTH}
                popoverWidth={FORM_FIELD_WIDTH}
                options={['YES', 'NO']}
                selectedOption={
                  isUndefined(scheduleSyncInterestValue) ? undefined : scheduleSyncInterestValue ? 'YES' : 'NO'
                }
                onOptionClick={(option) => {
                  const newValue = option === 'YES' ? true : false;
                  setValue('sync_schedule_interest', newValue, { shouldValidate: true, shouldDirty: true });
                }}
              />
            </HStack>
            <HStack gap={'20px'} width={'100%'}>
              <DropdownMultiSelect
                title="Appointment Types"
                height="50px"
                width="100%"
                popoverWidth={FORM_FIELD_WIDTH}
                options={APPOINTMENT_TYPES}
                selectedOptions={appointmentTypesValue?.filter((value): value is string => value !== undefined)}
                onOptionClick={(option) => {
                  const copy = [...(appointmentTypesValue ?? [])];
                  if (copy.includes(option)) {
                    copy.splice(copy.indexOf(option), 1);
                  } else {
                    copy.push(option);
                  }
                  setValue('appointment_types', copy, { shouldValidate: true, shouldDirty: true });
                }}
              />
              {isAppointmentTypeCustom && (
                <Input
                  {...register('custom_appointment_type')}
                  className="custom-input"
                  placeholder={'Other Appointment Type'}
                  fontSize={'16px'}
                  _placeholder={{
                    fontWeight: '600',
                    color: 'background.blueGray',
                  }}
                  data-state={
                    touchedFields.custom_appointment_type
                      ? errors.custom_appointment_type?.message
                        ? 'invalid'
                        : 'valid'
                      : ''
                  }
                />
              )}
            </HStack>

            <Input
              {...register('booking_link')}
              className="custom-input"
              placeholder={'Booking Link (optional)'}
              fontSize={'16px'}
              _placeholder={{
                fontWeight: '600',
                color: 'background.blueGray',
              }}
              data-state={touchedFields.booking_link ? (errors.booking_link?.message ? 'invalid' : 'valid') : ''}
            />

            <Text variant={'errorMedium'}>
              {errors.first_name?.message ||
                errors.last_name?.message ||
                errors.practice_name?.message ||
                errors.email?.message ||
                errors.designation?.message ||
                errors.bio?.message ||
                errors.schedule_tools?.message ||
                errors.ehr_software?.message ||
                errors.sync_schedule_interest?.message ||
                errors.appointment_types?.message ||
                errors.specialities?.message ||
                errors.availability?.message ||
                errors.booking_link?.message ||
                errors.population?.message}
            </Text>

            {/* {noLocationsAdded && <Text variant={'errorMedium'}>At least one location must be added.</Text>} */}

            {dropdownsErrorMessage && <Text variant={'errorMedium'}>{dropdownsErrorMessage}</Text>}

            <CustomButton
              backgroundColor="secondary.500"
              label={submitLabel}
              width={FORM_FIELD_WIDTH}
              disabled={isSubmitDisabled}
              // isLoading={isPending}
              buttonType={isSubmitDisabled ? 'button' : 'submit'}
            />
          </VStack>
        </form>
      </FormProvider>
    </VStack>
  );
};

export default ProviderForm;
